import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, PageViewport, RenderTask } from 'pdfjs-dist';
import type { DocumentSource, SizeUnits, ViewerMemoryStats } from '../models/viewer-types';
import { createRangedPDFSource } from '../pdf/pdf-byte-source';
import { createPDFOutline, type Destination, type PdfOutline } from '../pdf/pdf-outline';
import { createRemotePDFSource } from '../pdf/pdf-source';
import type { PdfjsModule } from '../pdf/pdf-types';
import { formatFileSize } from '../utils/file-size';
import { installPromiseTry } from '../utils/promise-try';
import { setToolbarIcon, type ToolbarIcon } from './viewer-icons';

/** What the bytes turned out to be, when the viewer can tell. */
export type DetectedMedia = { type: 'pdf' } | { type: 'image'; mime: string };

/** A source that can be read in parts, which is what lets a PDF open without downloading it whole. */
type RangedSource = DocumentSource & { readRange: NonNullable<DocumentSource['readRange']> };

export type MediaInput = ArrayBuffer | string | RangedSource;

export interface MediaViewerOptions {
  contentsVisible?: boolean;
  sizeUnits?: SizeUnits;
  controlsHost?: HTMLElement | null;
  pdfAssetsUrl?: string;
  timeoutMs?: number;
  maxFileBytes: number;
  onError: (error: Error) => void;
  onMatches: (value: string) => void;
  onInfo?: (value: string) => void;
  onProgress?: (loaded: number, total?: number) => void;
  onContentsVisibleChange?: (visible: boolean) => void;
  getQuery?: () => string;
}

/** What opening a document tells the caller about it. */
export interface MediaDetails {
  pages?: number;
  size?: number;
  width?: number;
  height?: number;
}

export interface MediaViewer {
  search(value: string, scrollToHit?: boolean): void;
  jump(): Promise<void>;
  updateInfo(): void;
  memoryStats(): ViewerMemoryStats;
  open(buffer: MediaInput, media: DetectedMedia, name: string): Promise<MediaDetails | undefined>;
  /** Builds the toolbar, which can only be laid out once the document's own shape is known. */
  finish(): void;
  setContentsVisible(value: boolean): void;
  setSizeUnits(value: SizeUnits): void;
  destroy(): void;
}

/** One page of a PDF: its placeholder, and whatever is currently drawn into it. */
interface PageEntry {
  number: number;
  element: HTMLDivElement;
  /** Bumped whenever the page is released, so that work in flight for it knows it is stale. */
  version: number;
  page?: PDFPageProxy | null;
  canvas?: HTMLCanvasElement | null;
  textLayer?: { cancel(): void } | null;
  renderTask?: RenderTask | null;
  promise?: Promise<void> | null;
}

/** Where one occurrence of the query sits: its page, its text item, and its place within that item. */
interface SearchHit {
  page: number;
  item: number;
  offset: number;
  occurrence: number;
}

/** Text of one page as PDF.js streams it, collected for the text layer to lay out. */
interface PageText {
  items: Array<unknown>;
  styles: Record<string, unknown>;
  lang: string | null;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 4;
const MAX_CANVAS_PIXELS = 4000000;
const DEFAULT_TIMEOUT_MS = 60000;
const INTERACTION_QUIET_MS = 80;
const BACKGROUND_BATCH = 4;
const SEARCH_BATCH = 4;

const PART_ATTRIBUTE = 'part';
const LABEL_ATTRIBUTE = 'aria-label';
const CANCELLED_RENDERS = ['RenderingCancelledException', 'AbortException'];

const imageExtensions: Record<string, string> = {
  png: 'png',
  apng: 'apng',
  jpg: 'jpeg',
  jpe: 'jpeg',
  pjpeg: 'jpeg',
  jpeg: 'jpeg',
  jfif: 'jpeg',
  gif: 'gif',
  webp: 'webp',
  avif: 'avif',
  svg: 'svg+xml',
  bmp: 'bmp',
  dib: 'bmp',
  ico: 'x-icon',
  cur: 'x-icon',
  tif: 'tiff',
  tiff: 'tiff',
  heic: 'heic',
  heif: 'heif',
  jxl: 'jxl',
  jp2: 'jp2',
  j2k: 'jp2',
  jpf: 'jpx',
  dng: 'x-raw',
  cr2: 'x-raw',
  cr3: 'x-raw',
  nef: 'x-raw',
  arw: 'x-raw',
  orf: 'x-raw',
  rw2: 'x-raw',
  psd: 'vnd.adobe.photoshop'
};

/**
 * The image type the leading bytes say it is.
 *
 * @param {Uint8Array} bytes The start of the file.
 * @param {Function} ascii Reads a byte range as Latin-1 text.
 * @return {string|undefined} The media type, or undefined when no signature matches.
 */
function signatureMime(bytes: Uint8Array, ascii: (start: number, end: number) => string): string | undefined {
  if (bytes[0] === 137 && ascii(1, 4) === 'PNG') return 'image/png';

  if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg';

  if (/^GIF8[79]a/.test(ascii(0, 6))) return 'image/gif';

  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';

  if (ascii(0, 2) === 'BM') return 'image/bmp';

  if (bytes[0] === 0 && bytes[1] === 0 && [1, 2].includes(bytes[2]) && bytes[3] === 0) return 'image/x-icon';

  // eslint-disable-next-line no-control-regex
  if (/^(II\*\x00|MM\x00\*)/.test(ascii(0, 4))) return 'image/tiff';

  if (ascii(4, 8) === 'ftyp') {
    const brands = ascii(8, 48);

    if (/avif|avis/.test(brands)) return 'image/avif';

    if (/heic|heix|hevc|hevx|mif1|msf1/.test(brands)) return 'image/heic';
  }

  return undefined;
}

/**
 * What the bytes are, read from the bytes themselves wherever possible and from the name or the
 * served type only as a fallback.
 *
 * @param {ArrayBuffer} buffer The start of the document, or all of it.
 * @param {string} [name] Name it arrived under.
 * @param {string} [contentType] Type the server declared.
 * @return {DetectedMedia|null} What to open it as, or null when this viewer cannot.
 */
export function detectMedia(buffer: ArrayBuffer, name = '', contentType = ''): DetectedMedia | null {
  const bytes = new Uint8Array(buffer);
  const ascii = (start: number, end: number): string => String.fromCharCode(...bytes.subarray(start, end));

  if (ascii(0, 1024).includes('%PDF-')) return { type: 'pdf' };

  let mime = signatureMime(bytes, ascii);
  const prefix = new TextDecoder().decode(bytes.subarray(0, 4096)).replace(/^\uFEFF/, '');

  if (!mime && /^\s*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(prefix)) mime = 'image/svg+xml';

  // A ZIP is an Office package or an archive, both of which belong to another reader entirely.
  if (!mime && bytes[0] === 80 && bytes[1] === 75) return null;

  const extension = name.split('.').pop()?.toLowerCase() ?? '';

  if (!mime && imageExtensions[extension]) mime = 'image/' + imageExtensions[extension];

  if (!mime && /^image\/[a-z0-9.+-]+$/i.test(contentType)) mime = contentType;

  if (mime) return { type: 'image', mime };

  if (extension === 'pdf' || contentType === 'application/pdf') return { type: 'pdf' };

  return null;
}

/**
 * A PDF or an image on screen: pages drawn as they come into view and released as they leave, text
 * extracted for search in the background, and the one worker or object URL it owns released on the
 * way out.
 *
 * @param {HTMLElement} root Where the document is drawn.
 * @param {MediaViewerOptions} options The toolbar's home, the limits, and where to report.
 * @return {MediaViewer} The document, and the ways to drive it.
 */
export function createMediaViewer(root: HTMLElement, options: MediaViewerOptions): MediaViewer {
  const document = root.ownerDocument;

  let active = true;
  let pdfjs: PdfjsModule | undefined;
  let task: PDFDocumentLoadingTask | undefined;
  let pdf: PDFDocumentProxy | undefined;
  let worker: Worker | undefined;
  let pdfWorker: InstanceType<PdfjsModule['PDFWorker']> | undefined;
  let objectUrl: string | undefined;
  let image: HTMLImageElement | undefined;
  let imageStage: HTMLDivElement | undefined;
  let imageRotation = 0;

  const rotations = new Map<number, number>();

  let pageNumber = 1;
  let scale = 1;
  let renderId = 0;
  let fit = true;
  let fitPage = true;
  let zoomValue: HTMLButtonElement | undefined;
  let fitButton: HTMLButtonElement | undefined;
  let zoomOut: HTMLButtonElement | undefined;
  let zoomIn: HTMLButtonElement | undefined;

  const viewportRoot = root.parentElement as HTMLElement;
  const networkController = new AbortController();

  let entries: Array<PageEntry> = [];
  let scrollFrame = 0;
  let resizeObserver: ResizeObserver | undefined;
  let ready = false;
  let rebuilding = false;

  const sizes = new Map<number, PageViewport>();
  const rendered = new Set<PageEntry>();

  let startupTimer: ReturnType<typeof setTimeout> | undefined;
  let rejectPending!: (reason?: unknown) => void;

  const cancelled = new Promise<never>((_, reject) => {
    rejectPending = reject;
  });

  cancelled.catch(() => undefined);

  const controls = document.createElement('div');

  controls.className = 'media-controls';
  controls.setAttribute(PART_ATTRIBUTE, 'viewer-media-controls');

  let outlineController: PdfOutline | null | undefined;

  const workspace = document.createElement('div');

  workspace.className = 'media-workspace';

  const surface = document.createElement('div');

  surface.className = 'media-surface';
  surface.setAttribute(PART_ATTRIBUTE, 'viewer-media-surface');
  workspace.append(surface);
  root.replaceChildren(workspace);
  (options.controlsHost || root).prepend(controls);

  const viewportControlHeight = (): number => (options.controlsHost ? 0 : controls.offsetHeight);

  let previousSurfaceWidth = surface.clientWidth;
  let searchHits: Array<SearchHit> = [];
  let hitIndex = -1;
  let query = '';
  let searchVersion = 0;
  let searching = false;
  let indexing = false;
  let cancelBackgroundWait = (): void => undefined;
  let lastInteraction = 0;
  let indexBytes = 0;
  let downloaded = 0;
  let totalBytes = 0;

  const textCache = new Map<number, Array<string | null>>();
  const textPending = new Map<number, Promise<PageText | undefined>>();
  const textReaders = new Set<ReadableStreamDefaultReader<PageText>>();

  const button = (caption: string, action: () => unknown): HTMLButtonElement => {
    const element = document.createElement('button');

    element.setAttribute(PART_ATTRIBUTE, 'viewer-button');
    element.textContent = caption;
    element.type = 'button';
    element.onclick = (): void => {
      Promise.resolve(action()).catch((error: Error) => {
        if (active && !CANCELLED_RENDERS.includes(error.name)) options.onError(error);
      });
    };
    controls.append(element);

    return element;
  };

  const label = document.createElement('span');

  label.setAttribute('aria-live', 'polite');

  const updateLabel = (): void => {
    if (!pdf && !image) return;

    if (pdf) {
      options.onInfo?.(
        `PDF · Page ${pageNumber} / ${pdf.numPages} · Loaded ${formatFileSize(downloaded, options.sizeUnits)} / ` +
          `${formatFileSize(totalBytes, options.sizeUnits)} · Text ${textCache.size} / ${pdf.numPages} pages`
      );
    }

    if (zoomValue) zoomValue.textContent = `${Math.round(scale * 100)}%`;

    if (zoomOut) zoomOut.disabled = scale <= MIN_SCALE;

    if (zoomIn) zoomIn.disabled = scale >= MAX_SCALE;

    if (fitButton) {
      const action = fit && !fitPage ? 'Fit page' : 'Fit width';

      fitButton.setAttribute(LABEL_ATTRIBUTE, action);
      fitButton.title = action;
      setToolbarIcon(fitButton, fit && !fitPage ? 'page' : 'width');
    }

    const turned = rotations.get(pageNumber);
    const pageAngle = turned ? ` · ${turned}°` : '';
    const imageAngle = imageRotation ? ` · ${imageRotation}°` : '';
    const shown = image as HTMLImageElement;

    label.textContent = pdf
      ? `Page ${pageNumber} / ${pdf.numPages}${pageAngle}`
      : `${shown.naturalWidth} × ${shown.naturalHeight}${imageAngle}`;
  };

  function updateMatches(): void {
    if (!query) {
      options.onMatches('');

      return;
    }

    const pages = (pdf as PDFDocumentProxy).numPages;
    const checked = textCache.size;
    const selected = searchHits[hitIndex];
    const partial = checked < pages;
    const suffix = `${partial ? ' (partial)' : ''} · Text ${checked} / ${pages} pages${indexing ? ' · Counting…' : ''}`;
    const headline = searchHits.length
      ? `${selected ? hitIndex + 1 : 0} / ${searchHits.length} matches`
      : partial
        ? 'No matches in read pages'
        : 'No matches';

    options.onMatches(headline + suffix);
  }

  function rebuildMatches(): void {
    const selected = searchHits[hitIndex];

    searchHits = [];

    if (query) {
      for (const [number, content] of [...textCache].sort((a, b) => a[0] - b[0])) {
        let occurrence = 0;

        for (const [item, value] of content.entries()) {
          if (typeof value !== 'string') continue;

          const text = value.toLocaleLowerCase();
          let offset = 0;
          let found = text.indexOf(query, offset);

          while (found !== -1) {
            searchHits.push({ page: number, item, offset: found, occurrence: occurrence++ });
            offset = found + query.length;
            found = text.indexOf(query, offset);
          }
        }
      }
    }

    hitIndex = selected
      ? searchHits.findIndex((hit) => hit.page === selected.page && hit.item === selected.item && hit.offset === selected.offset)
      : -1;
    highlightMatches();

    if (!searching) updateMatches();
  }

  // Several occurrences can belong to the same selectable PDF text span, so the span to mark as
  // current is found by counting the distinct items the earlier hits on this page belong to.
  function currentSpanIndex(hit: SearchHit): number {
    return new Set(searchHits.filter((other) => other.page === hit.page && other.item < hit.item).map((other) => other.item)).size;
  }

  function matchingSpans(entry: PageEntry): Array<Element> {
    return [...entry.element.querySelectorAll('.pdf-text span')].filter((span) =>
      (span.textContent ?? '').toLocaleLowerCase().includes(query)
    );
  }

  function highlightMatches(): void {
    for (const entry of rendered) {
      const spans = entry.element.querySelectorAll('.pdf-text span');

      for (const span of spans) {
        span.classList.toggle('media-hit', !!query && (span.textContent ?? '').toLocaleLowerCase().includes(query));
        span.classList.remove('current');
      }

      const hit = searchHits[hitIndex];

      if (hit?.page === entry.number) matchingSpans(entry)[currentSpanIndex(hit)]?.classList.add('current');
    }
  }

  async function readText(number: number, forRendering = false): Promise<PageText | undefined> {
    if (!forRendering && textCache.has(number)) return undefined;

    const known = textPending.get(number);

    if (known) return known;

    const pending = (async (): Promise<PageText | undefined> => {
      const page = await (pdf as PDFDocumentProxy).getPage(number);

      if (!active) return undefined;

      // Safari 18 lacks ReadableStream async iteration used by PDF.js getTextContent.
      const reader = page.streamTextContent().getReader() as ReadableStreamDefaultReader<PageText>;

      textReaders.add(reader);

      const content: PageText = { items: [], styles: Object.create(null), lang: null };

      try {
        while (active) {
          const { done, value } = await reader.read();

          if (done) break;

          if (!active) {
            await reader.cancel();

            return undefined;
          }

          content.lang ??= value.lang;
          Object.assign(content.styles, value.styles);
          content.items.push(...value.items);
        }
      } finally {
        textReaders.delete(reader);
        reader.releaseLock();
      }

      if (!active) return undefined;

      if (!textCache.has(number)) {
        // Keep only strings for search; layout, fonts, and transforms stay near the viewport.
        const strings = content.items.map((item) =>
          typeof (item as { str?: unknown }).str === 'string' ? ((item as { str: string }).str as string) : null
        );

        textCache.set(number, strings);
        indexBytes += strings.reduce((bytes, value) => bytes + (value?.length || 0) * 2, 0);
        rebuildMatches();
        updateLabel();
      }

      if (!rendered.has(entries[number - 1])) page.cleanup();

      return content;
    })().finally(() => textPending.delete(number));

    textPending.set(number, pending);

    return pending;
  }

  function backgroundTurn(): Promise<void> {
    const view = document.defaultView as Window;

    return new Promise<void>((resolve) => {
      const idle = typeof view.requestIdleCallback === 'function';
      const finish = (): void => {
        cancelBackgroundWait = (): void => undefined;
        resolve();
      };
      const handle = idle ? view.requestIdleCallback(finish, { timeout: 500 }) : view.setTimeout(finish, 32);

      cancelBackgroundWait = (): void => {
        if (idle) view.cancelIdleCallback(handle);
        else view.clearTimeout(handle);

        finish();
      };
    });
  }

  function busy(): boolean {
    return rebuilding || searching || performance.now() - lastInteraction < INTERACTION_QUIET_MS;
  }

  async function indexRemainingPages(): Promise<void> {
    const document = pdf as PDFDocumentProxy;

    while (active && textCache.size < document.numPages) {
      await backgroundTurn();

      if (!active) return;

      if (busy()) continue;

      // Let visible-page rendering win over background extraction.
      await Promise.all([...rendered].map((entry) => entry.promise).filter(Boolean));

      for (let count = 0; active && count < BACKGROUND_BATCH; count++) {
        if (busy()) break;

        let number = 1;

        while (number <= document.numPages && textCache.has(number)) number++;

        if (number > document.numPages) break;

        await readText(number);
      }
    }
  }

  function completeSearchIndex(): void {
    // Index automatically only after all bytes arrive; background preparation never fetches the rest
    // of a partial PDF.
    if (!active || !pdf || !ready || indexing || !totalBytes || downloaded < totalBytes || textCache.size === pdf.numPages) {
      return;
    }

    indexing = true;
    indexRemainingPages()
      .catch((error: Error) => {
        if (active) options.onError(error);
      })
      .finally(() => {
        indexing = false;

        if (!active) return;

        updateMatches();

        if (query && searchHits.length && hitIndex < 0) jump();
      });
    updateMatches();
  }

  function search(value: string, scrollToHit = true): void {
    const nextQuery = value.trim().toLocaleLowerCase();

    if (nextQuery !== query) {
      searchVersion++;
      searching = false;
      query = nextQuery;
      searchHits = [];
      hitIndex = -1;
    }

    rebuildMatches();

    if (query && scrollToHit && searchHits.length) jump();

    completeSearchIndex();
  }

  // Extract text in small batches without retaining raster buffers for those pages.
  async function readUntilHitFound(version: number): Promise<boolean> {
    const document = pdf as PDFDocumentProxy;

    while (hitIndex + 1 >= searchHits.length && textCache.size < document.numPages) {
      const unread: Array<number> = [];

      for (let number = 1; number <= document.numPages && unread.length < SEARCH_BATCH; number++) {
        if (!textCache.has(number)) unread.push(number);
      }

      options.onMatches(`Searching pages ${unread[0]}–${unread.at(-1)}…`);
      await Promise.all(unread.map((number) => readText(number)));

      if (!active || version !== searchVersion) return false;

      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    return active && version === searchVersion;
  }

  async function jump(): Promise<void> {
    if (!active || !pdf || !query || searching) return;

    const version = searchVersion;

    searching = true;

    try {
      if (!(await readUntilHitFound(version))) return;

      if (searchHits.length) {
        hitIndex = (hitIndex + 1) % searchHits.length;

        const hit = searchHits[hitIndex];

        await goToPage(hit.page);

        if (!active || version !== searchVersion) return;

        highlightMatches();

        const span = matchingSpans(entries[hit.page - 1])[currentSpanIndex(hit)];

        span?.classList.add('current');
        span?.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    } catch (error) {
      if (active && version === searchVersion) options.onError(error as Error);
    } finally {
      if (version === searchVersion) {
        searching = false;

        if (active) updateMatches();
      }
    }
  }

  function showImage(): void {
    if (!active || !image) return;

    const sideways = imageRotation % 180 !== 0;
    const width = sideways ? image.naturalHeight : image.naturalWidth;
    const height = sideways ? image.naturalWidth : image.naturalHeight;
    const imageScale = fit
      ? Math.min(
          1,
          Math.max(1, surface.clientWidth - 48) / width,
          fitPage ? Math.max(1, viewportRoot.clientHeight - viewportControlHeight() - 48) / height : Infinity
        )
      : scale;

    if (fit) scale = imageScale;

    const stage = imageStage as HTMLDivElement;

    stage.style.width = `${width * imageScale}px`;
    stage.style.height = `${height * imageScale}px`;
    image.style.width = `${image.naturalWidth * imageScale}px`;
    image.style.height = `${image.naturalHeight * imageScale}px`;
    image.style.transform = `translate(-50%, -50%) rotate(${imageRotation}deg)`;
    updateLabel();
  }

  function releaseEntry(entry: PageEntry): void {
    rendered.delete(entry);
    entry.version++;
    entry.renderTask?.cancel();
    entry.textLayer?.cancel();

    if (entry.canvas) {
      entry.canvas.width = 0;
      entry.canvas.height = 0;
    }

    entry.element.replaceChildren();
    entry.promise = null;
    entry.canvas = null;
    entry.textLayer = null;
    entry.renderTask = null;
    entry.page?.cleanup();
  }

  function renderEntry(entry: PageEntry): Promise<void> {
    if (entry.promise) return entry.promise;

    rendered.add(entry);

    const generation = renderId;
    const version = ++entry.version;
    const current = (): boolean => active && generation === renderId && version === entry.version;

    entry.promise = (async (): Promise<void> => {
      const page = await (pdf as PDFDocumentProxy).getPage(entry.number);

      if (!current()) return;

      entry.page = page;

      const dimensions = page.getViewport({ scale: 1 });

      sizes.set(entry.number, dimensions);

      const viewport = page.getViewport({
        scale,
        rotation: (page.rotate + (rotations.get(entry.number) || 0)) % 360
      });

      entry.element.style.width = `${viewport.width}px`;
      entry.element.style.height = `${viewport.height}px`;

      const canvas = document.createElement('canvas');

      canvas.setAttribute(LABEL_ATTRIBUTE, `PDF page ${entry.number}`);

      // Only nearby pages retain raster buffers; each raster has a fixed pixel budget.
      const ratio = Math.min(devicePixelRatio || 1, 2, Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height)));

      canvas.width = Math.max(1, Math.floor(viewport.width * ratio));
      canvas.height = Math.max(1, Math.floor(viewport.height * ratio));
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      const text = document.createElement('div');

      text.className = 'pdf-text';
      entry.canvas = canvas;
      entry.element.append(canvas, text);
      entry.renderTask = page.render({
        canvas,
        viewport,
        transform: [ratio, 0, 0, ratio, 0, 0],
        annotationMode: 0
      });
      await entry.renderTask.promise;

      if (!current()) return;

      const content = await readText(entry.number, true);

      if (!current()) return;

      const layer = new (pdfjs as PdfjsModule).TextLayer({
        textContentSource: content as never,
        container: text,
        viewport
      });

      entry.textLayer = layer;
      await layer.render();

      if (current()) highlightMatches();
    })().catch((error: Error) => {
      if (current() && !CANCELLED_RENDERS.includes(error.name)) throw error;
    });

    return entry.promise;
  }

  function refreshVisible(): void {
    if (!active || !pdf || rebuilding || !entries.length) return;

    // Ignore a small trailing fragment of the previous page after centering a search hit.
    const top =
      viewportRoot.getBoundingClientRect().top + viewportControlHeight() + Math.max(1, Math.min(64, viewportRoot.clientHeight / 4));

    // Binary search avoids scanning every page on each scroll in large PDFs.
    let low = 0;
    let high = entries.length - 1;

    while (low < high) {
      const middle = (low + high) >> 1;

      if (entries[middle].element.getBoundingClientRect().bottom <= top) low = middle + 1;
      else high = middle;
    }

    const changed = pageNumber !== low + 1;

    pageNumber = low + 1;
    updateLabel();

    const start = Math.max(0, low - 1);
    const end = Math.min(entries.length - 1, low + 1);

    for (const entry of [...rendered]) {
      if (entry.number - 1 < start || entry.number - 1 > end) releaseEntry(entry);
    }

    for (let index = start; index <= end; index++) {
      renderEntry(entries[index]).catch((error: Error) => {
        if (active) options.onError(error);
      });
    }

    if (changed) highlightMatches();

    outlineController?.sync();
  }

  function scheduleVisible(): void {
    lastInteraction = performance.now();
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(refreshVisible);
  }

  async function goToPage(number: number): Promise<void> {
    pageNumber = Math.max(1, Math.min((pdf as PDFDocumentProxy).numPages, number));

    const entry = entries[pageNumber - 1];
    const delta = entry.element.getBoundingClientRect().top - viewportRoot.getBoundingClientRect().top - viewportControlHeight() - 12;

    viewportRoot.scrollTop += delta;
    updateLabel();
    await renderEntry(entry);
    scheduleVisible();
  }

  function placeholdersFor(natural: PageViewport): DocumentFragment {
    const fragment = document.createDocumentFragment();

    entries = Array.from({ length: (pdf as PDFDocumentProxy).numPages }, (_, index) => {
      const size = sizes.get(index + 1) || natural;
      const sideways = (rotations.get(index + 1) || 0) % 180 !== 0;
      const element = document.createElement('div');

      element.className = 'pdf-page';
      element.dataset.page = String(index + 1);
      element.dataset.label = `Page ${index + 1}`;
      element.setAttribute('role', 'region');
      element.setAttribute(LABEL_ATTRIBUTE, `PDF page ${index + 1}`);
      element.style.width = `${(sideways ? size.height : size.width) * scale}px`;
      element.style.height = `${(sideways ? size.width : size.height) * scale}px`;
      element.style.setProperty('--total-scale-factor', String(scale));
      fragment.append(element);

      return { number: index + 1, element, version: 0 };
    });

    return fragment;
  }

  async function drawPage(): Promise<void> {
    const generation = ++renderId;

    rebuilding = true;

    try {
      for (const entry of entries) releaseEntry(entry);

      const page = await (pdf as PDFDocumentProxy).getPage(pageNumber);

      if (!active || generation !== renderId) return;

      const natural = page.getViewport({ scale: 1 });

      sizes.set(pageNumber, natural);

      const dimensions = page.getViewport({
        scale: 1,
        rotation: (page.rotate + (rotations.get(pageNumber) || 0)) % 360
      });

      if (fit) {
        scale = Math.min(
          2,
          Math.max(
            MIN_SCALE,
            Math.min(
              (surface.clientWidth - 48) / dimensions.width,
              fitPage ? (viewportRoot.clientHeight - viewportControlHeight() - 24) / dimensions.height : Infinity
            )
          )
        );
      }

      surface.replaceChildren(placeholdersFor(natural));
      await goToPage(pageNumber);
    } finally {
      if (generation === renderId) rebuilding = false;
    }
  }

  function outlinePosition(): { page: number; top: number; displayTop: number } | null {
    const entry = entries[pageNumber - 1];

    if (!entry) return null;

    const displayTop = Math.max(
      0,
      (viewportRoot.getBoundingClientRect().top + viewportControlHeight() + 12 - entry.element.getBoundingClientRect().top) / scale
    );
    let top = displayTop;

    if (entry.page) {
      const normal = entry.page.getViewport({ scale: 1 });
      const rotated = entry.page.getViewport({
        scale: 1,
        rotation: (entry.page.rotate + (rotations.get(pageNumber) || 0)) % 360
      });
      const point = rotated.convertToPdfPoint(0, displayTop);

      top = Math.max(0, normal.convertToViewportPoint(point[0], point[1])[1]);
    }

    return { page: pageNumber, top, displayTop };
  }

  function layoutChanged(): void {
    outlineController?.resize();
    root.style.setProperty('--pdf-controls-height', `${viewportControlHeight()}px`);
    root.style.setProperty('--pdf-panel-height', `${Math.max(120, viewportRoot.clientHeight - viewportControlHeight())}px`);

    const width = surface.clientWidth;
    const widthChanged = Math.abs(width - previousSurfaceWidth) > 1;

    previousSurfaceWidth = width;

    if (!active || !pdf || !ready || !fit || !widthChanged) return;

    const position = outlinePosition();
    const drawing = drawPage();
    const generation = renderId;

    drawing
      .then(() => {
        if (!active || generation !== renderId || !position || position.page !== pageNumber) return;

        const entry = entries[pageNumber - 1];

        viewportRoot.scrollTop +=
          entry.element.getBoundingClientRect().top -
          viewportRoot.getBoundingClientRect().top -
          viewportControlHeight() -
          12 +
          position.displayTop * scale;
        scheduleVisible();
        outlineController?.sync(true);
      })
      .catch((error: Error) => {
        if (active) options.onError(error);
      });
  }

  // PDF.js dropped convertToViewportRectangle; each corner through convertToViewportPoint is what it did.
  function viewportRectangle(viewport: PageViewport, rectangle: Array<number>): Array<number> {
    const [firstX, firstY] = viewport.convertToViewportPoint(rectangle[0], rectangle[1]);
    const [secondX, secondY] = viewport.convertToViewportPoint(rectangle[2], rectangle[3]);

    return [firstX, firstY, secondX, secondY];
  }

  async function navigateDestination(destination: Destination, current: () => boolean): Promise<void> {
    const document = pdf as PDFDocumentProxy;
    const ref = destination[0];
    const index = Number.isInteger(ref) ? ref : await document.getPageIndex(ref);

    if (!current()) return;

    if (!Number.isInteger(index) || index < 0 || index >= document.numPages) throw Error('Invalid bookmark page.');

    const page = await document.getPage(index + 1);

    if (!current()) return;

    const dimensions = page.getViewport({
      scale: 1,
      rotation: (page.rotate + (rotations.get(index + 1) || 0)) % 360
    });
    const type = destination[1]?.name;
    const [xMin, , , yMax] = page.view;
    const availableWidth = Math.max(1, surface.clientWidth - 48);
    const availableHeight = Math.max(1, viewportRoot.clientHeight - viewportControlHeight() - 24);
    const finite = (value: number, fallback: number): number => (Number.isFinite(value) ? value : fallback);

    let x = xMin;
    let y = yMax;

    fit = false;

    switch (type) {
      case 'XYZ':
        x = finite(destination[2], xMin);
        y = finite(destination[3], yMax);

        if (Number.isFinite(destination[4]) && destination[4] > 0) {
          scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, destination[4]));
        }

        break;
      case 'Fit':
      case 'FitB':
        scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, availableWidth / dimensions.width, availableHeight / dimensions.height));
        break;
      case 'FitH':
      case 'FitBH':
        fit = true;
        y = finite(destination[2], yMax);
        break;
      case 'FitV':
      case 'FitBV':
        scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, availableHeight / dimensions.height));
        x = finite(destination[2], xMin);
        break;
      case 'FitR': {
        const corners = [destination[2], destination[3], destination[4], destination[5]];

        if (!corners.every(Number.isFinite)) throw Error('Invalid bookmark rectangle.');

        const points = viewportRectangle(dimensions, corners);
        const width = Math.abs(points[2] - points[0]);
        const height = Math.abs(points[3] - points[1]);

        if (!width || !height) throw Error('Invalid bookmark rectangle.');

        scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, availableWidth / width, availableHeight / height));
        x = Math.min(corners[0], corners[2]);
        y = Math.max(corners[1], corners[3]);
        break;
      }
      default:
        throw Error('Unsupported bookmark destination.');
    }

    pageNumber = index + 1;
    await drawPage();

    if (!current() || !entries[index]) return;

    const targetViewport = page.getViewport({
      scale,
      rotation: (page.rotate + (rotations.get(index + 1) || 0)) % 360
    });
    const rectangle = type === 'FitR' ? viewportRectangle(targetViewport, destination.slice(2, 6)) : null;
    const point = rectangle
      ? [Math.min(rectangle[0], rectangle[2]), Math.min(rectangle[1], rectangle[3])]
      : targetViewport.convertToViewportPoint(x, y);
    const element = entries[index].element;

    viewportRoot.scrollTop +=
      element.getBoundingClientRect().top - viewportRoot.getBoundingClientRect().top - viewportControlHeight() - 12 + Math.max(0, point[1]);
    viewportRoot.scrollLeft +=
      element.getBoundingClientRect().left - viewportRoot.getBoundingClientRect().left - 24 + Math.max(0, point[0]);
    scheduleVisible();
  }

  function zoom(multiplier: number): Promise<void> | void {
    fit = false;
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * multiplier));

    return pdf ? drawPage() : showImage();
  }

  function assetsBase(): URL {
    const base = new URL(options.pdfAssetsUrl || './pdfjs/', document.baseURI);

    if (base.origin !== location.origin || !['http:', 'https:'].includes(base.protocol)) {
      throw Error('PDF assets must be hosted on the same origin as the application.');
    }

    if (!base.pathname.endsWith('/')) base.pathname += '/';

    return base;
  }

  async function pdfSourceFor(
    buffer: MediaInput,
    module: PdfjsModule
  ): Promise<ReturnType<typeof createRangedPDFSource> extends Promise<infer T> ? T : never> {
    if (typeof buffer === 'string') {
      return createRemotePDFSource(buffer, module, {
        maxFileBytes: options.maxFileBytes,
        timeoutMs: options.timeoutMs,
        signal: networkController.signal,
        abort: () => networkController.abort(),
        onProgress: reportProgress,
        onError: reportSourceError
      });
    }

    return createRangedPDFSource(buffer as RangedSource, module, {
      maxFileBytes: options.maxFileBytes,
      timeoutMs: options.timeoutMs,
      signal: networkController.signal,
      abort: () => networkController.abort(),
      onProgress: reportProgress,
      onError: reportSourceError
    });
  }

  function reportProgress(loaded: number, total?: number): void {
    downloaded = loaded;
    totalBytes = total || loaded;
    options.onProgress?.(loaded, total);
    updateLabel();
    completeSearchIndex();
  }

  function reportSourceError(error: Error): void {
    rejectPending(error);

    if (ready) options.onError(error);
  }

  function attachOutline(): void {
    createPDFOutline({
      pdf: pdf as PDFDocumentProxy,
      document,
      controls,
      workspace,
      viewport: viewportRoot,
      navigate: navigateDestination,
      onLayout: layoutChanged,
      isActive: () => active,
      getPosition: outlinePosition,
      onVisibleChange: (visible: boolean) => {
        if (active) options.onContentsVisibleChange?.(visible);
      }
    })
      .then((value) => {
        if (active) {
          outlineController = value;
          outlineController?.setVisible(Boolean(options.contentsVisible));
          layoutChanged();
        } else value?.destroy();
      })
      .catch(() => {
        /* An invalid outline must not prevent reading the PDF. */
      });
  }

  async function openPdf(buffer: MediaInput): Promise<MediaDetails | undefined> {
    fitPage = false;
    installPromiseTry();

    const base = assetsBase();

    try {
      pdfjs = await import('pdfjs-dist');
    } catch {
      throw Error('Unable to load the PDF renderer.');
    }

    if (!active) return undefined;

    const module = pdfjs;

    worker = new Worker(new URL('pdf.worker.mjs', base), { type: 'module' });
    worker.addEventListener(
      'error',
      (event) => {
        event.preventDefault();
        rejectPending(Error('Unable to start the PDF worker. Check the packaged PDF assets and their MIME types.'));
      },
      { once: true }
    );
    pdfWorker = new module.PDFWorker({ port: worker as never });

    const remote = typeof buffer === 'string' || typeof (buffer as RangedSource)?.readRange === 'function';
    const source = remote
      ? await pdfSourceFor(buffer, module)
      : { data: new Uint8Array(buffer as ArrayBuffer), size: (buffer as ArrayBuffer).byteLength };

    if (!active) return undefined;

    totalBytes = source.size;

    if (!remote) downloaded = source.size;

    task = module.getDocument({
      ...('range' in source ? { range: source.range, length: source.length } : { data: source.data }),
      disableStream: true,
      disableAutoFetch: true,
      rangeChunkSize: 65536,
      worker: pdfWorker,
      cMapUrl: new URL('cmaps/', base).href,
      cMapPacked: true,
      standardFontDataUrl: new URL('standard_fonts/', base).href,
      wasmUrl: new URL('wasm/', base).href,
      isEvalSupported: false,
      enableXfa: false,
      useSystemFonts: true,
      maxImageSize: 32000000
    } as never);
    task.onPassword = (): void => rejectPending(Error('Password-protected PDFs are not supported.'));
    startupTimer = setTimeout(
      () => rejectPending(Error('PDF opening exceeded the configured timeout.')),
      options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    );

    try {
      pdf = await Promise.race([task.promise, cancelled]);
    } finally {
      clearTimeout(startupTimer);
    }

    if (!active) return undefined;

    viewportRoot.addEventListener('scroll', scheduleVisible, { passive: true });
    await drawPage();
    resizeObserver = new ResizeObserver(layoutChanged);
    resizeObserver.observe(viewportRoot);
    ready = true;
    scheduleVisible();
    completeSearchIndex();
    attachOutline();

    return { pages: pdf.numPages, size: source.size };
  }

  async function openImage(buffer: ArrayBuffer, media: { mime: string }, name: string): Promise<MediaDetails | undefined> {
    objectUrl = URL.createObjectURL(new Blob([buffer], { type: media.mime }));

    const element = document.createElement('img');

    image = element;
    element.alt = name;
    element.className = 'preview-image';
    element.src = objectUrl;
    await element.decode().catch(() => {
      throw Error(
        'This image is unsupported by this browser or is damaged. HEIC, TIFF, JPEG XL, and RAW support varies; ' +
          'convert the file to PNG, JPEG, or WebP.'
      );
    });

    if (!active) return undefined;

    imageStage = document.createElement('div');
    imageStage.className = 'image-stage';
    imageStage.append(element);
    surface.append(imageStage);
    resizeObserver = new ResizeObserver(() => {
      if (fit) showImage();
    });
    resizeObserver.observe(viewportRoot);
    showImage();

    return { width: element.naturalWidth, height: element.naturalHeight };
  }

  function rotate(delta: number): Promise<void> | void {
    if (pdf) {
      const currentPage = pageNumber;

      rotations.set(currentPage, ((rotations.get(currentPage) || 0) + delta + 360) % 360);

      return drawPage();
    }

    imageRotation = (imageRotation + delta + 360) % 360;
    showImage();
  }

  return {
    search,
    jump,
    updateInfo: updateLabel,
    memoryStats(): ViewerMemoryStats {
      return {
        textIndexBytes: indexBytes,
        canvasBytes: [...rendered].reduce((bytes, entry) => bytes + (entry.canvas ? entry.canvas.width * entry.canvas.height * 4 : 0), 0),
        indexedPages: textCache.size,
        totalPages: pdf?.numPages || 0,
        indexing
      };
    },
    open(buffer: MediaInput, media: DetectedMedia, name: string): Promise<MediaDetails | undefined> {
      return media.type === 'pdf' ? openPdf(buffer) : openImage(buffer as ArrayBuffer, media, name);
    },
    finish(): void {
      zoomOut = button('Zoom out', () => zoom(1 / 1.25));
      zoomOut.textContent = '−';
      zoomValue = button('100%', () => {
        fit = false;
        scale = 1;

        return pdf ? drawPage() : showImage();
      });
      zoomValue.className = 'zoom-value';
      zoomValue.title = 'Reset zoom to 100%';
      zoomIn = button('Zoom in', () => zoom(1.25));
      zoomIn.textContent = '+';

      const zoomButtons: Array<[HTMLButtonElement, string]> = [
        [zoomOut, 'Zoom out'],
        [zoomValue, '100%'],
        [zoomIn, 'Zoom in']
      ];

      for (const [element, name] of zoomButtons) {
        element.setAttribute(LABEL_ATTRIBUTE, name);

        if (!element.title) element.title = name;
      }

      const divider = document.createElement('i');

      divider.className = 'control-divider';
      divider.setAttribute('aria-hidden', 'true');
      controls.append(divider);
      fitButton = button('Fit width', () => {
        fitPage = fit && !fitPage;
        fit = true;

        return pdf ? drawPage() : showImage();
      });

      const left = button('Rotate left', () => rotate(-90));
      const right = button('Rotate right', () => rotate(90));
      const rotateButtons: Array<[HTMLButtonElement, ToolbarIcon, string]> = [
        [left, 'left', 'Rotate left'],
        [right, 'right', 'Rotate right']
      ];

      for (const [element, kind, name] of rotateButtons) {
        element.setAttribute(LABEL_ATTRIBUTE, name);
        setToolbarIcon(element, kind);
      }

      left.title = pdf ? 'Rotate current page 90° counterclockwise' : 'Rotate image 90° counterclockwise';
      right.title = pdf ? 'Rotate current page 90° clockwise' : 'Rotate image 90° clockwise';
      updateLabel();
      controls.append(label);

      if (pdf) layoutChanged();
      else showImage();
    },
    setContentsVisible(value: boolean): void {
      options.contentsVisible = Boolean(value);
      outlineController?.setVisible(options.contentsVisible);
    },
    setSizeUnits(value: SizeUnits): void {
      options.sizeUnits = value;
      updateLabel();
    },
    destroy(): void {
      active = false;
      cancelBackgroundWait();
      searchVersion++;
      indexBytes = 0;
      textCache.clear();

      for (const reader of textReaders) reader.cancel().catch(() => undefined);

      textReaders.clear();
      clearTimeout(startupTimer);
      rejectPending(new DOMException('Cancelled', 'AbortError'));
      renderId++;
      cancelAnimationFrame(scrollFrame);
      viewportRoot.removeEventListener('scroll', scheduleVisible);
      resizeObserver?.disconnect();
      outlineController?.destroy();

      for (const entry of entries) releaseEntry(entry);

      entries = [];
      networkController.abort();

      if (task) task.destroy().catch(() => undefined);

      pdfWorker?.destroy();
      worker?.terminate();

      if (objectUrl) URL.revokeObjectURL(objectUrl);

      image?.removeAttribute('src');
      controls.remove();
      root.replaceChildren();
    }
  };
}
